import { makeStyles } from "@material-ui/core/styles";

/**
 * Color palette system for RevenueCommandCenter component
 * Organized into Grey (Blue-Grey), Blue (Brand Primary), Orange, Red, Green, Light Green, and Lime scales
 * Plus additional Material Design palettes from the screenshot
 *
 * @example
 * // Method 1: Direct import for inline styles or dynamic values
 * import { rccColors, grey, blue, orange, red, green, lightGreen, lime } from './colors';
 *
 * function MyComponent() {
 *   return (
 *     <Box style={{ color: rccColors.neutral[700] }}>
 *       <Typography style={{ backgroundColor: rccColors.blue[500] }}>
 *         Text with blue background
 *       </Typography>
 *       <div style={{ color: rccColors.orange[200] }}>
 *         Quota color text
 *       </div>
 *     </Box>
 *   );
 * }
 *
 * @example
 * // Method 2: Using the hook for Material-UI className
 * import { useRccColors } from './colors';
 *
 * function MyComponent() {
 *   const colors = useRccColors();
 *
 *   return (
 *     <Box>
 *       <Typography className={colors.grey700}>
 *         Grey text
 *       </Typography>
 *       <Typography className={colors.blue500}>
 *         Blue text
 *       </Typography>
 *       <Box className={colors.bgGrey50}>
 *         Light grey background
 *       </Box>
 *       <Box className={colors.bgBlue500}>
 *         Blue background
 *       </Box>
 *     </Box>
 *   );
 * }
 *
 * @example
 * // Method 3: Combining with makeStyles
 * import { makeStyles } from '@material-ui/core/styles';
 * import { rccColors } from './colors';
 *
 * const useStyles = makeStyles((theme) => ({
 *   customCard: {
 *     backgroundColor: rccColors.neutral[50],
 *     color: rccColors.neutral[900],
 *     border: `1px solid ${rccColors.blue[500]}`,
 *   },
 * }));
 */

// Grey scale (50-900)
export const grey = {
  50: "#ECEFF1",
  100: "#CFD8DC",
  200: "#B0BEC5",
  300: "#90A4AE",
  400: "#78909C",
  500: "#607D8B",
  600: "#546E7A",
  700: "#455A64",
  800: "#37474F",
  900: "#263238",
};

// Blue (Brand Primary Color) scale (50-900)
export const blue = {
  50: "#E3F2FD",
  100: "#BBDEFB",
  200: "#90CAF9",
  300: "#64B5F6",
  400: "#42A5F5",
  500: "#2196F3",
  600: "#1E88E5",
  700: "#1976D2",
  800: "#1565C0",
  900: "#0D47A1",
};

// Orange scale (50-900)
export const orange = {
  50: "#FFF3E0",
  100: "#FFE0B2",
  200: "#FFCC80",
  300: "#FFB74D",
  400: "#FFA726",
  500: "#FF9800",
  600: "#FB8C00",
  700: "#F57C00",
  800: "#EF6C00",
  900: "#E65100",
};

// Red scale (50-900)
export const red = {
  50: "#FFEBEE",
  100: "#FFCDD2",
  200: "#EF9A9A",
  300: "#E57373",
  400: "#EF5350",
  500: "#F44336",
  600: "#E53935",
  700: "#D32F2F",
  800: "#C62828",
  900: "#B71C1C",
};

// Green scale (50-900)
export const green = {
  50: "#E8F5E9",
  100: "#C8E6C9",
  200: "#A5D6A7",
  300: "#81C784",
  400: "#66BB6A",
  500: "#4CAF50",
  600: "#43A047",
  700: "#388E3C",
  800: "#2E7D32",
  900: "#1B5E20",
};

// Light Green scale (50-900)
export const lightGreen = {
  50: "#F1F8E9",
  100: "#DCEDC8",
  200: "#C5E1A5",
  300: "#AED581",
  400: "#9CCC65",
  500: "#8BC34A",
  600: "#7CB342",
  700: "#689F38",
  800: "#55882F",
  900: "#33691E",
};

// Lime scale (50-900)
export const lime = {
  50: "#F9FBE7",
  100: "#F0F4C3",
  200: "#E6EE9C",
  300: "#DCE775",
  400: "#D4E157",
  500: "#CDDC39",
  600: "#C0CA33",
  700: "#AFB42B",
  800: "#9E9D24",
  900: "#827717",
};

// Light Blue scale (50-900)
export const lightBlue = {
  50: "#E1F5FE",
  100: "#B3E5FC",
  200: "#81D4FA",
  300: "#4FC3F7",
  400: "#29B6F6",
  500: "#03A9F4",
  600: "#039BE5",
  700: "#0288D1",
  800: "#0277BD",
  900: "#01579B",
};

// Teal scale (50-900)
export const teal = {
  50: "#E0F2F1",
  100: "#B2DFDB",
  200: "#80CBC4",
  300: "#46B7AB",
  400: "#26A69A",
  500: "#009688",
  600: "#00897B",
  700: "#00796B",
  800: "#00695C",
  900: "#004D40",
};

// Cyan scale (50-900)
export const cyan = {
  50: "#E0F7FA",
  100: "#B2EBF2",
  200: "#80DEEA",
  300: "#4DD0E1",
  400: "#26C6DA",
  500: "#00BCD4",
  600: "#00ACC1",
  700: "#0097A7",
  800: "#00838F",
  900: "#006064",
};

// Indigo scale (50-900)
export const indigo = {
  50: "#E8EAF6",
  100: "#C5CAE9",
  200: "#9FA8DA",
  300: "#7986CB",
  400: "#5C6BC0",
  500: "#3F51B5",
  600: "#3949AB",
  700: "#303F9F",
  800: "#283593",
  900: "#1A237E",
};

// Purple scale (50-900)
export const purple = {
  50: "#F3E5F5",
  100: "#E1BEE7",
  200: "#CE93D8",
  300: "#BA68C8",
  400: "#AB47BC",
  500: "#9C27B0",
  600: "#8E24AA",
  700: "#7B1FA2",
  800: "#6A1B9A",
  900: "#4A148C",
};

// Deep Purple scale (50-900)
export const deepPurple = {
  50: "#EDE7F6",
  100: "#D1C4E9",
  200: "#B39DDB",
  300: "#9575CD",
  400: "#7E57C2",
  500: "#673AB7",
  600: "#5E35B1",
  700: "#512DA8",
  800: "#4527A0",
  900: "#311B92",
};

// Pink scale (50-900)
export const pink = {
  50: "#FCE4EC",
  100: "#F8BBD0",
  200: "#FF8EB4",
  300: "#F06292",
  400: "#EC407A",
  500: "#E91E63",
  600: "#D81B60",
  700: "#C2185B",
  800: "#AD1457",
  900: "#880E4F",
};

// Deep Orange scale (50-900)
export const deepOrange = {
  50: "#FBE9E7",
  100: "#FFCCBC",
  200: "#FFAB91",
  300: "#FF8A65",
  400: "#FF7043",
  500: "#FF5722",
  600: "#F4511E",
  700: "#E64A19",
  800: "#D84315",
  900: "#BF360C",
};

// Amber scale (50-900)
export const amber = {
  50: "#FFF8E1",
  100: "#FFECB3",
  200: "#FFE082",
  300: "#FFD54F",
  400: "#FFCA28",
  500: "#FFC107",
  600: "#FFB300",
  700: "#FFA000",
  800: "#FF8F00",
  900: "#FF6F00",
};

// Yellow scale (50-900)
export const yellow = {
  50: "#FFFDE7",
  100: "#FFF9C4",
  200: "#FFF59D",
  300: "#FFF176",
  400: "#FFEE58",
  500: "#FFEB3B",
  600: "#FDD835",
  700: "#FBC02D",
  800: "#F9A825",
  900: "#F57F17",
};

// Material Grey scale (50-900)
export const materialGrey = {
  0: "#FFFFFF",
  50: "#FAFAFA",
  100: "#F5F5F5",
  200: "#EEEEEE",
  300: "#E0E0E0",
  400: "#BDBDBD",
  500: "#9E9E9E",
  600: "#757575",
  700: "#616161",
  800: "#424242",
  900: "#212121",
};

// Brown scale (50-900)
export const brown = {
  50: "#EFEBE9",
  100: "#D7CCC8",
  200: "#BCAAA4",
  300: "#A1887F",
  400: "#8D6E63",
  500: "#795548",
  600: "#6D4C41",
  700: "#5D4037",
  800: "#4E342E",
  900: "#3E2723",
};

// Paired palette for Stacked Bar Charts
// Each item contains the background color for the bar and the text color for the label
export const stackedBarPairedPalette = [
  { color: "#689EF7", textColor: "#F0FAFF" }, // Pair 1
  { color: "#FCBC05", textColor: "#FFFFFF" }, // Pair 2
  { color: "#5EB977", textColor: "#F6FFF6" }, // Pair 3
  { color: "#FF8A37", textColor: "#FFFFFF" }, // Pair 4
  { color: "#46BDC6", textColor: "#FFFFFF" }, // Pair 5
  { color: "#9450CB", textColor: "#FFFFFF" }, // Pair 6
  { color: "#96BBF9", textColor: "#01579A" }, // Pair 7
  { color: "#F3958F", textColor: "#B71C1D" }, // Pair 8
  { color: "#FDD973", textColor: "#8E2A0B" }, // Pair 9
  { color: "#8DCFA0", textColor: "#1B5F21" }, // Pair 10
];

// Linearity Chart Paired Palette
export const linearityPairedPalette = {
  quota: {
    color: orange[200],
    textColor: "#8E2A0B",
  },
  wonQTD: {
    color: lightGreen[100],
    textColor: "#1B5F21",
  },
  wonEOQ: {
    color: green[600],
    textColor: "#F6FFF6",
  },
};

// Qualified Pipeline Coverage Paired Palette
export const pipelineCoveragePairedPalette = {
  quota: {
    color: orange[200],
    textColor: "#8E2A0B",
  },
  won: {
    color: green[500],
    textColor: "#F6FFF6",
  },
  pipeline: {
    color: blue[500],
    textColor: "#F0FAFF",
  },
};

// Combined color palette object for easy access
export const rccColors = {
  neutral: grey, // Blue-Grey scale (backwards compatibility)
  materialGrey: materialGrey,
  blue: blue,
  lightBlue: lightBlue,
  orange: orange,
  deepOrange: deepOrange,
  red: red,
  green: green,
  lightGreen: lightGreen,
  lime: lime,
  teal: teal,
  cyan: cyan,
  indigo: indigo,
  purple: purple,
  deepPurple: deepPurple,
  pink: pink,
  amber: amber,
  yellow: yellow,
  brown: brown,
  stackedBarPairedPalette: stackedBarPairedPalette,
  linearityPairedPalette: linearityPairedPalette,
  pipelineCoveragePairedPalette: pipelineCoveragePairedPalette,
};

/**
 * Hook that returns color classes for common use cases
 * Usage: const colors = useRccColors(); colors.grey50, colors.blue500, etc.
 */
export const useRccColors = makeStyles(() => ({
  // Grey classes
  grey50: { color: grey[50] },
  grey100: { color: grey[100] },
  grey200: { color: grey[200] },
  grey300: { color: grey[300] },
  grey400: { color: grey[400] },
  grey500: { color: grey[500] },
  grey600: { color: grey[600] },
  grey700: { color: grey[700] },
  grey800: { color: grey[800] },
  grey900: { color: grey[900] },

  // Blue classes
  blue50: { color: blue[50] },
  blue100: { color: blue[100] },
  blue200: { color: blue[200] },
  blue300: { color: blue[300] },
  blue400: { color: blue[400] },
  blue500: { color: blue[500] },
  blue600: { color: blue[600] },
  blue700: { color: blue[700] },
  blue800: { color: blue[800] },
  blue900: { color: blue[900] },

  // Background color classes
  bgGrey50: { backgroundColor: grey[50] },
  bgGrey100: { backgroundColor: grey[100] },
  bgGrey200: { backgroundColor: grey[200] },
  bgGrey300: { backgroundColor: grey[300] },
  bgGrey400: { backgroundColor: grey[400] },
  bgGrey500: { backgroundColor: grey[500] },
  bgGrey600: { backgroundColor: grey[600] },
  bgGrey700: { backgroundColor: grey[700] },
  bgGrey800: { backgroundColor: grey[800] },
  bgGrey900: { backgroundColor: grey[900] },

  bgBlue50: { backgroundColor: blue[50] },
  bgBlue100: { backgroundColor: blue[100] },
  bgBlue200: { backgroundColor: blue[200] },
  bgBlue300: { backgroundColor: blue[300] },
  bgBlue400: { backgroundColor: blue[400] },
  bgBlue500: { backgroundColor: blue[500] },
  bgBlue600: { backgroundColor: blue[600] },
  bgBlue700: { backgroundColor: blue[700] },
  bgBlue800: { backgroundColor: blue[800] },
  bgBlue900: { backgroundColor: blue[900] },

  // Light Green classes
  lightGreen50: { color: lightGreen[50] },
  lightGreen100: { color: lightGreen[100] },
  lightGreen200: { color: lightGreen[200] },
  lightGreen300: { color: lightGreen[300] },
  lightGreen400: { color: lightGreen[400] },
  lightGreen500: { color: lightGreen[500] },
  lightGreen600: { color: lightGreen[600] },
  lightGreen700: { color: lightGreen[700] },
  lightGreen800: { color: lightGreen[800] },
  lightGreen900: { color: lightGreen[900] },

  // Lime classes
  lime50: { color: lime[50] },
  lime100: { color: lime[100] },
  lime200: { color: lime[200] },
  lime300: { color: lime[300] },
  lime400: { color: lime[400] },
  lime500: { color: lime[500] },
  lime600: { color: lime[600] },
  lime700: { color: lime[700] },
  lime800: { color: lime[800] },
  lime900: { color: lime[900] },

  // Light Green background classes
  bgLightGreen50: { backgroundColor: lightGreen[50] },
  bgLightGreen100: { backgroundColor: lightGreen[100] },
  bgLightGreen200: { backgroundColor: lightGreen[200] },
  bgLightGreen300: { backgroundColor: lightGreen[300] },
  bgLightGreen400: { backgroundColor: lightGreen[400] },
  bgLightGreen500: { backgroundColor: lightGreen[500] },
  bgLightGreen600: { backgroundColor: lightGreen[600] },
  bgLightGreen700: { backgroundColor: lightGreen[700] },
  bgLightGreen800: { backgroundColor: lightGreen[800] },
  bgLightGreen900: { backgroundColor: lightGreen[900] },

  // Lime background classes
  bgLime50: { backgroundColor: lime[50] },
  bgLime100: { backgroundColor: lime[100] },
  bgLime200: { backgroundColor: lime[200] },
  bgLime300: { backgroundColor: lime[300] },
  bgLime400: { backgroundColor: lime[400] },
  bgLime500: { backgroundColor: lime[500] },
  bgLime600: { backgroundColor: lime[600] },
  bgLime700: { backgroundColor: lime[700] },
  bgLime800: { backgroundColor: lime[800] },
  bgLime900: { backgroundColor: lime[900] },

  // Light Blue classes
  lightBlue50: { color: lightBlue[50] },
  lightBlue100: { color: lightBlue[100] },
  lightBlue200: { color: lightBlue[200] },
  lightBlue300: { color: lightBlue[300] },
  lightBlue400: { color: lightBlue[400] },
  lightBlue500: { color: lightBlue[500] },
  lightBlue600: { color: lightBlue[600] },
  lightBlue700: { color: lightBlue[700] },
  lightBlue800: { color: lightBlue[800] },
  lightBlue900: { color: lightBlue[900] },
  bgLightBlue50: { backgroundColor: lightBlue[50] },
  bgLightBlue100: { backgroundColor: lightBlue[100] },
  bgLightBlue200: { backgroundColor: lightBlue[200] },
  bgLightBlue300: { backgroundColor: lightBlue[300] },
  bgLightBlue400: { backgroundColor: lightBlue[400] },
  bgLightBlue500: { backgroundColor: lightBlue[500] },
  bgLightBlue600: { backgroundColor: lightBlue[600] },
  bgLightBlue700: { backgroundColor: lightBlue[700] },
  bgLightBlue800: { backgroundColor: lightBlue[800] },
  bgLightBlue900: { backgroundColor: lightBlue[900] },

  // Teal classes
  teal50: { color: teal[50] },
  teal100: { color: teal[100] },
  teal200: { color: teal[200] },
  teal300: { color: teal[300] },
  teal400: { color: teal[400] },
  teal500: { color: teal[500] },
  teal600: { color: teal[600] },
  teal700: { color: teal[700] },
  teal800: { color: teal[800] },
  teal900: { color: teal[900] },
  bgTeal50: { backgroundColor: teal[50] },
  bgTeal100: { backgroundColor: teal[100] },
  bgTeal200: { backgroundColor: teal[200] },
  bgTeal300: { backgroundColor: teal[300] },
  bgTeal400: { backgroundColor: teal[400] },
  bgTeal500: { backgroundColor: teal[500] },
  bgTeal600: { backgroundColor: teal[600] },
  bgTeal700: { backgroundColor: teal[700] },
  bgTeal800: { backgroundColor: teal[800] },
  bgTeal900: { backgroundColor: teal[900] },

  // Cyan classes
  cyan50: { color: cyan[50] },
  cyan100: { color: cyan[100] },
  cyan200: { color: cyan[200] },
  cyan300: { color: cyan[300] },
  cyan400: { color: cyan[400] },
  cyan500: { color: cyan[500] },
  cyan600: { color: cyan[600] },
  cyan700: { color: cyan[700] },
  cyan800: { color: cyan[800] },
  cyan900: { color: cyan[900] },
  bgCyan50: { backgroundColor: cyan[50] },
  bgCyan100: { backgroundColor: cyan[100] },
  bgCyan200: { backgroundColor: cyan[200] },
  bgCyan300: { backgroundColor: cyan[300] },
  bgCyan400: { backgroundColor: cyan[400] },
  bgCyan500: { backgroundColor: cyan[500] },
  bgCyan600: { backgroundColor: cyan[600] },
  bgCyan700: { backgroundColor: cyan[700] },
  bgCyan800: { backgroundColor: cyan[800] },
  bgCyan900: { backgroundColor: cyan[900] },

  // Indigo classes
  indigo50: { color: indigo[50] },
  indigo100: { color: indigo[100] },
  indigo200: { color: indigo[200] },
  indigo300: { color: indigo[300] },
  indigo400: { color: indigo[400] },
  indigo500: { color: indigo[500] },
  indigo600: { color: indigo[600] },
  indigo700: { color: indigo[700] },
  indigo800: { color: indigo[800] },
  indigo900: { color: indigo[900] },
  bgIndigo50: { backgroundColor: indigo[50] },
  bgIndigo100: { backgroundColor: indigo[100] },
  bgIndigo200: { backgroundColor: indigo[200] },
  bgIndigo300: { backgroundColor: indigo[300] },
  bgIndigo400: { backgroundColor: indigo[400] },
  bgIndigo500: { backgroundColor: indigo[500] },
  bgIndigo600: { backgroundColor: indigo[600] },
  bgIndigo700: { backgroundColor: indigo[700] },
  bgIndigo800: { backgroundColor: indigo[800] },
  bgIndigo900: { backgroundColor: indigo[900] },

  // Purple classes
  purple50: { color: purple[50] },
  purple100: { color: purple[100] },
  purple200: { color: purple[200] },
  purple300: { color: purple[300] },
  purple400: { color: purple[400] },
  purple500: { color: purple[500] },
  purple600: { color: purple[600] },
  purple700: { color: purple[700] },
  purple800: { color: purple[800] },
  purple900: { color: purple[900] },
  bgPurple50: { backgroundColor: purple[50] },
  bgPurple100: { backgroundColor: purple[100] },
  bgPurple200: { backgroundColor: purple[200] },
  bgPurple300: { backgroundColor: purple[300] },
  bgPurple400: { backgroundColor: purple[400] },
  bgPurple500: { backgroundColor: purple[500] },
  bgPurple600: { backgroundColor: purple[600] },
  bgPurple700: { backgroundColor: purple[700] },
  bgPurple800: { backgroundColor: purple[800] },
  bgPurple900: { backgroundColor: purple[900] },

  // Deep Purple classes
  deepPurple50: { color: deepPurple[50] },
  deepPurple100: { color: deepPurple[100] },
  deepPurple200: { color: deepPurple[200] },
  deepPurple300: { color: deepPurple[300] },
  deepPurple400: { color: deepPurple[400] },
  deepPurple500: { color: deepPurple[500] },
  deepPurple600: { color: deepPurple[600] },
  deepPurple700: { color: deepPurple[700] },
  deepPurple800: { color: deepPurple[800] },
  deepPurple900: { color: deepPurple[900] },
  bgDeepPurple50: { backgroundColor: deepPurple[50] },
  bgDeepPurple100: { backgroundColor: deepPurple[100] },
  bgDeepPurple200: { backgroundColor: deepPurple[200] },
  bgDeepPurple300: { backgroundColor: deepPurple[300] },
  bgDeepPurple400: { backgroundColor: deepPurple[400] },
  bgDeepPurple500: { backgroundColor: deepPurple[500] },
  bgDeepPurple600: { backgroundColor: deepPurple[600] },
  bgDeepPurple700: { backgroundColor: deepPurple[700] },
  bgDeepPurple800: { backgroundColor: deepPurple[800] },
  bgDeepPurple900: { backgroundColor: deepPurple[900] },

  // Pink classes
  pink50: { color: pink[50] },
  pink100: { color: pink[100] },
  pink200: { color: pink[200] },
  pink300: { color: pink[300] },
  pink400: { color: pink[400] },
  pink500: { color: pink[500] },
  pink600: { color: pink[600] },
  pink700: { color: pink[700] },
  pink800: { color: pink[800] },
  pink900: { color: pink[900] },
  bgPink50: { backgroundColor: pink[50] },
  bgPink100: { backgroundColor: pink[100] },
  bgPink200: { backgroundColor: pink[200] },
  bgPink300: { backgroundColor: pink[300] },
  bgPink400: { backgroundColor: pink[400] },
  bgPink500: { backgroundColor: pink[500] },
  bgPink600: { backgroundColor: pink[600] },
  bgPink700: { backgroundColor: pink[700] },
  bgPink800: { backgroundColor: pink[800] },
  bgPink900: { backgroundColor: pink[900] },

  // Deep Orange classes
  deepOrange50: { color: deepOrange[50] },
  deepOrange100: { color: deepOrange[100] },
  deepOrange200: { color: deepOrange[200] },
  deepOrange300: { color: deepOrange[300] },
  deepOrange400: { color: deepOrange[400] },
  deepOrange500: { color: deepOrange[500] },
  deepOrange600: { color: deepOrange[600] },
  deepOrange700: { color: deepOrange[700] },
  deepOrange800: { color: deepOrange[800] },
  deepOrange900: { color: deepOrange[900] },
  bgDeepOrange50: { backgroundColor: deepOrange[50] },
  bgDeepOrange100: { backgroundColor: deepOrange[100] },
  bgDeepOrange200: { backgroundColor: deepOrange[200] },
  bgDeepOrange300: { backgroundColor: deepOrange[300] },
  bgDeepOrange400: { backgroundColor: deepOrange[400] },
  bgDeepOrange500: { backgroundColor: deepOrange[500] },
  bgDeepOrange600: { backgroundColor: deepOrange[600] },
  bgDeepOrange700: { backgroundColor: deepOrange[700] },
  bgDeepOrange800: { backgroundColor: deepOrange[800] },
  bgDeepOrange900: { backgroundColor: deepOrange[900] },

  // Amber classes
  amber50: { color: amber[50] },
  amber100: { color: amber[100] },
  amber200: { color: amber[200] },
  amber300: { color: amber[300] },
  amber400: { color: amber[400] },
  amber500: { color: amber[500] },
  amber600: { color: amber[600] },
  amber700: { color: amber[700] },
  amber800: { color: amber[800] },
  amber900: { color: amber[900] },
  bgAmber50: { backgroundColor: amber[50] },
  bgAmber100: { backgroundColor: amber[100] },
  bgAmber200: { backgroundColor: amber[200] },
  bgAmber300: { backgroundColor: amber[300] },
  bgAmber400: { backgroundColor: amber[400] },
  bgAmber500: { backgroundColor: amber[500] },
  bgAmber600: { backgroundColor: amber[600] },
  bgAmber700: { backgroundColor: amber[700] },
  bgAmber800: { backgroundColor: amber[800] },
  bgAmber900: { backgroundColor: amber[900] },

  // Yellow classes
  yellow50: { color: yellow[50] },
  yellow100: { color: yellow[100] },
  yellow200: { color: yellow[200] },
  yellow300: { color: yellow[300] },
  yellow400: { color: yellow[400] },
  yellow500: { color: yellow[500] },
  yellow600: { color: yellow[600] },
  yellow700: { color: yellow[700] },
  yellow800: { color: yellow[800] },
  yellow900: { color: yellow[900] },
  bgYellow50: { backgroundColor: yellow[50] },
  bgYellow100: { backgroundColor: yellow[100] },
  bgYellow200: { backgroundColor: yellow[200] },
  bgYellow300: { backgroundColor: yellow[300] },
  bgYellow400: { backgroundColor: yellow[400] },
  bgYellow500: { backgroundColor: yellow[500] },
  bgYellow600: { backgroundColor: yellow[600] },
  bgYellow700: { backgroundColor: yellow[700] },
  bgYellow800: { backgroundColor: yellow[800] },
  bgYellow900: { backgroundColor: yellow[900] },

  // Material Grey classes
  materialGrey50: { color: materialGrey[50] },
  materialGrey100: { color: materialGrey[100] },
  materialGrey200: { color: materialGrey[200] },
  materialGrey300: { color: materialGrey[300] },
  materialGrey400: { color: materialGrey[400] },
  materialGrey500: { color: materialGrey[500] },
  materialGrey600: { color: materialGrey[600] },
  materialGrey700: { color: materialGrey[700] },
  materialGrey800: { color: materialGrey[800] },
  materialGrey900: { color: materialGrey[900] },
  bgMaterialGrey50: { backgroundColor: materialGrey[50] },
  bgMaterialGrey100: { backgroundColor: materialGrey[100] },
  bgMaterialGrey200: { backgroundColor: materialGrey[200] },
  bgMaterialGrey300: { backgroundColor: materialGrey[300] },
  bgMaterialGrey400: { backgroundColor: materialGrey[400] },
  bgMaterialGrey500: { backgroundColor: materialGrey[500] },
  bgMaterialGrey600: { backgroundColor: materialGrey[600] },
  bgMaterialGrey700: { backgroundColor: materialGrey[700] },
  bgMaterialGrey800: { backgroundColor: materialGrey[800] },
  bgMaterialGrey900: { backgroundColor: materialGrey[900] },

  // Brown classes
  brown50: { color: brown[50] },
  brown100: { color: brown[100] },
  brown200: { color: brown[200] },
  brown300: { color: brown[300] },
  brown400: { color: brown[400] },
  brown500: { color: brown[500] },
  brown600: { color: brown[600] },
  brown700: { color: brown[700] },
  brown800: { color: brown[800] },
  brown900: { color: brown[900] },
  bgBrown50: { backgroundColor: brown[50] },
  bgBrown100: { backgroundColor: brown[100] },
  bgBrown200: { backgroundColor: brown[200] },
  bgBrown300: { backgroundColor: brown[300] },
  bgBrown400: { backgroundColor: brown[400] },
  bgBrown500: { backgroundColor: brown[500] },
  bgBrown600: { backgroundColor: brown[600] },
  bgBrown700: { backgroundColor: brown[700] },
  bgBrown800: { backgroundColor: brown[800] },
  bgBrown900: { backgroundColor: brown[900] },
}));
